import React, { useEffect, useState } from 'react';
import { BookOpenIcon, InfoIcon, LightbulbIcon, BookmarkIcon, ArrowRightIcon } from 'lucide-react';
import { Link } from 'react-router-dom';
export const HeroSection = () => {
  const [activeInfoIndex, setActiveInfoIndex] = useState(0);
  const healthInfo = [{
    title: 'Understanding Biofilms',
    text: "How bacterial protective layers affect women's health",
    icon: <InfoIcon className="h-5 w-5 text-indigo-500" data-id="element-2487" />
  }, {
    title: 'Menopause & Vaginal Health',
    text: 'The science behind hormonal changes and tissue health',
    icon: <LightbulbIcon className="h-5 w-5 text-amber-500" data-id="element-2488" />
  }, {
    title: 'Evidence-Based Approaches',
    text: 'Research-backed information for informed decisions',
    icon: <BookmarkIcon className="h-5 w-5 text-blue-500" data-id="element-2489" />
  }];
  // Auto-rotate info cards every 5 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      setActiveInfoIndex(prev => (prev + 1) % healthInfo.length);
    }, 5000);
    return () => clearInterval(interval);
  }, []);
  return <section className="mb-8 sm:mb-12" data-id="element-2490">
      <div className="relative" data-id="element-2491">
        <img src="https://images.unsplash.com/photo-1571019613454-1cb2f99b2d8b?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2070&q=80" alt="Group of diverse women in their 40s and 50s discussing health information" className="w-full h-auto object-cover rounded-lg" style={{
        maxHeight: '500px',
        minHeight: '300px'
      }} data-id="element-2492" />
        <div className="absolute inset-0 bg-gradient-to-r from-slate-900/80 to-slate-800/40 rounded-lg flex items-center" data-id="element-2493">
          <div className="p-4 sm:p-6 md:p-10 max-w-lg" data-id="element-2494">
            <h1 className="text-xl sm:text-2xl md:text-3xl lg:text-4xl font-serif font-bold text-white leading-tight mb-2 sm:mb-4" data-id="element-2495">
              Evidence-Based Women's Health Information
            </h1>
            <p className="text-white/90 text-base sm:text-lg mb-4 sm:mb-6" data-id="element-2496">
              Discover scientifically-accurate information about vaginal health,
              menopause, and hormonal balance from medical experts
            </p>
            <div className="flex flex-wrap gap-2 sm:gap-3" data-id="element-2497">
              <Link to="/adv-1" className="inline-flex items-center bg-rose-500 text-white font-medium px-4 py-2 sm:px-5 sm:py-3 rounded-md hover:bg-rose-600 transition text-sm sm:text-base" data-id="element-2498">
                Women's Health Science
                <ArrowRightIcon className="h-4 w-4 sm:h-5 sm:w-5 ml-2" data-id="element-2499" />
              </Link>
              <Link to="/menopause" className="inline-flex items-center bg-rose-500 text-white font-medium px-4 py-2 sm:px-5 sm:py-3 rounded-md hover:bg-rose-600 transition text-sm sm:text-base" data-id="element-2500">
                Menopause Science
                <ArrowRightIcon className="h-4 w-4 sm:h-5 sm:w-5 ml-2" data-id="element-2501" />
              </Link>
            </div>
          </div>
        </div>
      </div>
      {/* Educational Information Cards */}
      <div className="mt-4 py-4 px-5 bg-white rounded-lg border border-slate-200 shadow-sm" data-id="element-2502">
        <div className="flex flex-col sm:flex-row items-center justify-between" data-id="element-2503">
          <div className="text-sm text-slate-700 font-medium mb-3 sm:mb-0" data-id="element-2504">
            TOPICS COVERED IN OUR RESEARCH:
          </div>
          <div className="w-full sm:w-auto" data-id="element-2505">
            <div className="relative h-16 sm:h-14 overflow-hidden w-full sm:w-80" data-id="element-2506">
              {healthInfo.map((info, index) => <div key={index} className={`absolute inset-0 flex items-center justify-center transition-all duration-500 ease-in-out ${index === activeInfoIndex ? 'opacity-100 transform translate-y-0' : 'opacity-0 transform translate-y-8'}`} data-id="element-2507">
                  <div className="flex items-center" data-id="element-2508">
                    <div className="mr-3" data-id="element-2509">{info.icon}</div>
                    <div data-id="element-2510">
                      <div className="text-base font-medium text-slate-900" data-id="element-2511">
                        {info.title}
                      </div>
                      <div className="text-xs text-slate-600" data-id="element-2512">{info.text}</div>
                    </div>
                  </div>
                </div>)}
            </div>
            <div className="flex justify-center mt-2" data-id="element-2513">
              {healthInfo.map((_, index) => <button key={index} className={`w-2 h-2 rounded-full mx-1 ${index === activeInfoIndex ? 'bg-indigo-500' : 'bg-slate-300'}`} onClick={() => setActiveInfoIndex(index)} aria-label={`View information ${index + 1}`} data-id="element-2514" />)}
            </div>
          </div>
        </div>
      </div>
      {/* Educational Resource Categories */}
      <div className="mt-4 grid grid-cols-1 sm:grid-cols-3 gap-3" data-id="element-2515">
        <div className="bg-slate-50 rounded-lg p-4 border border-slate-200 hover:border-indigo-300 hover:bg-indigo-50 transition-colors duration-300 cursor-pointer group" data-id="element-2516">
          <div className="flex items-center mb-2" data-id="element-2517">
            <div className="w-12 h-12 rounded-full bg-indigo-100 flex items-center justify-center mr-3 group-hover:scale-110 transition-transform" data-id="element-2518">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" className="text-indigo-700" data-id="element-2519">
                <path d="M12 22C17.5228 22 22 17.5228 22 12C22 6.47715 17.5228 2 12 2C6.47715 2 2 6.47715 2 12C2 17.5228 6.47715 22 12 22Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-2520" />
                <path d="M9.09 9C9.3251 8.33167 9.78915 7.76811 10.4 7.40913C11.0108 7.05016 11.7289 6.91894 12.4272 7.03871C13.1255 7.15848 13.7588 7.52152 14.2151 8.06353C14.6713 8.60553 14.9211 9.29152 14.92 10C14.92 12 11.92 13 11.92 13" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-2521" />
                <path d="M12 17H12.01" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-2522" />
              </svg>
            </div>
            <h3 className="font-medium text-slate-900" data-id="element-2523">Scientific Articles</h3>
          </div>
          <p className="text-sm text-slate-600 group-hover:text-slate-900" data-id="element-2524">
            Peer-reviewed research on women's health topics explained in clear,
            accessible language
          </p>
        </div>
        <div className="bg-slate-50 rounded-lg p-4 border border-slate-200 hover:border-blue-300 hover:bg-blue-50 transition-colors duration-300 cursor-pointer group" data-id="element-2525">
          <div className="flex items-center mb-2" data-id="element-2526">
            <div className="w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center mr-3 group-hover:scale-110 transition-transform" data-id="element-2527">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" className="text-blue-700" data-id="element-2528">
                <path d="M20.42 4.58C19.9183 4.07659 19.3222 3.67707 18.6658 3.40455C18.0094 3.13203 17.3057 2.99178 16.595 2.992C15.0367 2.992 13.5467 3.62533 12.5 4.725C11.4533 3.62533 9.96333 2.992 8.40501 2.992C7.69435 2.99178 6.99065 3.13203 6.33421 3.40455C5.67776 3.67707 5.08167 4.07659 4.58 4.58C1.75 7.41 1.75 11.915 4.58 14.748L12.5 22.667L20.42 14.748C23.25 11.915 23.25 7.41 20.42 4.58Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-2529" />
              </svg>
            </div>
            <h3 className="font-medium text-slate-900" data-id="element-2530">Health Guides</h3>
          </div>
          <p className="text-sm text-slate-600 group-hover:text-slate-900" data-id="element-2531">
            Comprehensive guides on managing common women's health conditions
            with evidence-based approaches
          </p>
        </div>
        <div className="bg-slate-50 rounded-lg p-4 border border-slate-200 hover:border-green-300 hover:bg-green-50 transition-colors duration-300 cursor-pointer group" data-id="element-2532">
          <div className="flex items-center mb-2" data-id="element-2533">
            <div className="w-12 h-12 rounded-full bg-green-100 flex items-center justify-center mr-3 group-hover:scale-110 transition-transform" data-id="element-2534">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" className="text-green-700" data-id="element-2535">
                <path d="M12 22C17.5228 22 22 17.5228 22 12C22 6.47715 17.5228 2 12 2C6.47715 2 2 6.47715 2 12C2 17.5228 6.47715 22 12 22Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-2536" />
                <path d="M7.5 12L10.5 15L16.5 9" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-2537" />
              </svg>
            </div>
            <h3 className="font-medium text-slate-900" data-id="element-2538">Expert Interviews</h3>
          </div>
          <p className="text-sm text-slate-600 group-hover:text-slate-900" data-id="element-2539">
            Insights from leading OB/GYNs and women's health researchers on the
            latest medical advances
          </p>
        </div>
      </div>
      {/* Research Highlight */}
      <div className="mt-4 text-center border-t border-b border-slate-200 py-4 px-3 bg-white hover:bg-slate-50 transition-colors duration-300 cursor-pointer" data-id="element-2540">
        <p className="font-serif text-slate-800 text-base sm:text-lg" data-id="element-2541">
          Recent research from Harvard Medical School shows{' '}
          <span className="bg-yellow-100 px-1 font-semibold relative group" data-id="element-2542">
            biofilm disruption
            <span className="absolute left-0 -bottom-12 w-64 bg-white p-2 rounded shadow-md text-xs text-left opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all z-10 border border-slate-200" data-id="element-2543">
              Bacterial biofilms are protective layers that shield bacteria from
              treatments, contributing to recurring infections. New research
              shows enzyme-based approaches can break these barriers.
            </span>
          </span>{' '}
          may be the key to addressing recurring vaginal infections
        </p>
      </div>
    </section>;
};